import React, { useState } from 'react';
import { supabase } from '../../config/supabase';
import { PREMIUM_SIZES, PERGOLA_HEIGHT } from '../../utils/pergolaCatalog';

const PergolasHeroSection: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [bestTime, setBestTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const price = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(PREMIUM_SIZES[0].priceCAD); // Use 10x10 premium to match product showcase



  const handleCallbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phone.trim()) {
      setShowError(true);
      return;
    }

    setIsSubmitting(true);
    setShowError(false);

    try {
      const cleanPhone = phone.replace(/\D/g, '');

      const insertData = {
        phone: cleanPhone,
        best_time: bestTime || 'anytime',
        source: 'pergolas_hero_section'
      };

      const { error } = await supabase
        .from('quick_callbacks')
        .insert([insertData]);

      if (error) {
        console.error('Supabase error:', error);
        setShowError(true);
      } else {
        setShowSuccess(true);
        setPhone('');
        setBestTime('');

        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting callback request:', error);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="relative py-20 min-h-screen flex items-center"
      style={{
        background: "linear-gradient(135deg, rgba(26, 26, 26, 0.7), rgba(26, 26, 26, 0.5)), url('images/pergolas/pic1.webp') center/cover"
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center relative z-10">
          {/* Hero Title */}
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Luxury Pergolas Built for Canadian Winters
          </h1>
          <p className="text-xl md:text-2xl text-white mb-2">Maintenance-Free Shade, All Year Long</p>
          <p className="text-lg text-white/90 mb-8 max-w-3xl mx-auto">
            Leading Ontario pergola installers serving Toronto and GTA. Choose Premium motorized pergolas or an individually quoted Premium+ project — engineered for Canadian weather, professionally installed, and built to last decades without maintenance.
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
            <div className="bg-white/95 rounded-xl p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-primary mb-2">Premium</h3>
              <div className="text-2xl font-bold text-accent mb-2">From {price} CAD</div>
              <p className="text-gray-600 text-sm mb-3">3×3 m footprint (approx. 10×10 ft). Standard installation and delivery included. Before tax.</p>
              <p className="text-gray-600 text-sm mb-4">Height: {PERGOLA_HEIGHT}</p>
              <a href="/pergola-quote" className="font-semibold text-accent underline">Get an instant estimate</a>
            </div>
            <div className="bg-white/95 rounded-xl p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-primary mb-2">Premium+</h3>
              <div className="text-2xl font-bold text-accent mb-2">Individually quoted</div>
              <p className="text-gray-600 text-sm mb-4">Tell us about your project for a personalized proposal and specification.</p>
              <a href="/pergola-quote?model=premium-plus" className="font-semibold text-accent underline">Request a Premium+ quote</a>
            </div>
          </div>

          {/* Quick Callback Form */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl p-8 max-w-md mx-auto mb-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-primary mb-4">Get Expert Advice</h3>
            <p className="text-gray-600 mb-6">Speak with our pergola specialists within 24 hours</p>

            <form onSubmit={handleCallbackSubmit} className="space-y-4">
              <div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Your phone number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  required
                />
              </div>

              <div>
                <select
                  value={bestTime}
                  onChange={(e) => setBestTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  <option value="">Best time to call (optional)</option>
                  <option value="morning">Morning (9 AM - 12 PM)</option>
                  <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                  <option value="evening">Evening (5 PM - 8 PM)</option>
                  <option value="anytime">Anytime</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : '📞 Get Expert Callback'}
              </button>
            </form>

            {showSuccess && (
              <div className="mt-3 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                <p>✅ Thank you! Our pergola expert will call you within 24 hours.</p>
              </div>
            )}

            {showError && (
              <div className="mt-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <p>❌ Something went wrong. Please try again.</p>
              </div>
            )}
          </div>

          {/* Additional CTA Options */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a
              href="/pergola-quote"
              className="bg-white text-primary px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block shadow-lg"
            >
              📋 Get Instant Quote
            </a>
            <a
              href="pergola-comparison.html"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary transition-colors inline-block"
            >
              📊 Compare Models
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 text-white mb-2">
              <span className="text-2xl">⭐</span>
              <span className="font-semibold">Ontario's Leading Pergola Installers • 47+ Happy Customers</span>
            </div>
            <div className="flex items-center gap-1 text-yellow-400">
              <span>★★★★★</span>
              <span className="text-white ml-2">4.9/5 Customer Rating</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PergolasHeroSection;
